<template>
  <TemplateBase>
    <main class="col-span-full row-start-2 bg-white border-b-2 border-solid border-app-green-100 md:bg-transparent md:border-b-0">
      <!-- Breadcrumbs -->
      <!-- TODO: back-end fix breadcrumbs + mobile back link -->
      <OrganismNavigationBreadcrumb class="mb-2 mx-auto p-4 md:container md:mb-8 md:pb-0 md:pt-10 xl:mb-20" :path="breadcrumbs" />

      <section class="md:container md:grid md:gap-x-4 md:grid-cols-12 md:mx-auto md:px-4 lg:gap-x-8">
        <header class="flex flex-col 2xl:col-span-3 2xl:col-start-2 mb-5 px-4 md:col-span-4 md:row-end-2 md:row-start-1 md:items-center md:mb-6 md:px-0">
          <div class="relative flex items-center pb-4">
            <MoleculePicture
              v-if="page.profileImage"
              class="profile-picture relative z-10 block shrink-0 mr-3 border-4 border-solid border-app-green-100 rounded-full overflow-hidden md:mr-0 md:border-white"
              :image="page.profileImage"
              :alt="page.title"
            />
            <picture
              v-else-if="get(page, 'animalCategory.icon.slug')"
              class="profile-picture relative z-10 flex shrink-0 items-center justify-center mr-3 bg-app-green-200 border-4 border-solid border-app-green-100 rounded-full overflow-hidden md:mr-0 md:border-white"
            >
              <AtomIcon :icon="page.animalCategory.icon.slug" class="w-10 h-10 lg:w-16 lg:h-16 text-app-green" />
            </picture>
            <MoleculeFavorite :animal-id="page.animalId" class="absolute bottom-0 left-0 shrink-0 -ml-2 mb-8 md:left-auto md:right-0 md:ml-auto" />
            <div class="md:hidden">
              <MoleculeBadge v-if="['missing', 'found'].includes(animalType) && page.animalFound" class="mb-2" background="bg-app-lime" :large="true" color="text-black">
                {{ t_('status-reunited') }}
              </MoleculeBadge>
              <MoleculeBadge v-else-if="isAvailableSoon(page.animalAvailability)" class="mb-2" background="bg-app-green" :large="true" color="text-black">
                {{ t_('status-available-soon') }}
              </MoleculeBadge>
              <MoleculeBadge v-else-if="isReserved(page.animalAvailability)" class="mb-2" :large="true" background="bg-app-gray-silverstone" color="text-white">
                {{ t_('status-reserved') }}
              </MoleculeBadge>
              <MoleculeBadge v-else-if="isPlaced(page.animalAvailability)" class="mb-2" :large="true" background="bg-app-gray-silverstone" color="text-white">
                {{ t_('status-placed') }}
              </MoleculeBadge>
              <MoleculeBadge v-else-if="isReactionStop(page.animalAvailability)" class="mb-2" :large="true" background="bg-app-gray-silverstone" color="text-white">
                {{ t_('status-reaction-stop') }}
              </MoleculeBadge>
              <MoleculeBadge v-else-if="page.animalMisfit" class="mb-2" :large="true" background="bg-app-lime" color="text-black">
                {{ t_('status-misfit') }}
              </MoleculeBadge>
              <MoleculeBadge v-else-if="page.animalHighlighted" class="mb-2" :large="true" background="bg-app-green" color="text-black">
                {{ t_('status-highlighted') }}
              </MoleculeBadge>
              <MoleculeBadge v-else-if="isNewInShelter(page.postDate, page.animalAvailability)" class="mb-2" background="bg-app-green" :large="true" color="text-black">
                {{ t_('status-new') }}
              </MoleculeBadge>
              <h1 v-if="animalType !== 'found'" class="items-center text-xl font-bold leading-none uppercase">{{ page.title }}</h1>
            </div>
          </div>
          <div class="c-header-content-container relative z-0 flex flex-col items-center bg-white rounded-xl">
            <div class="hidden flex-col items-center md:flex">
              <MoleculeBadge v-if="['missing', 'found'].includes(animalType) && page.animalFound" class="mb-2 lg:mb-3" background="bg-app-lime" :large="true" color="text-black">
                {{ t_('status-reunited') }}
              </MoleculeBadge>
              <MoleculeBadge v-else-if="isAvailableSoon(page.animalAvailability)" class="mb-2 lg:mb-3" background="bg-app-green" :large="true" color="text-white">
                {{ t_('status-available-soon') }}
              </MoleculeBadge>
              <MoleculeBadge v-else-if="isReserved(page.animalAvailability)" class="mb-2 lg:mb-3" :large="true" background="bg-app-gray-silverstone" color="text-white">
                {{ t_('status-reserved') }}
              </MoleculeBadge>
              <MoleculeBadge v-else-if="isPlaced(page.animalAvailability)" class="mb-2 lg:mb-3" :large="true" background="bg-app-gray-silverstone" color="text-white">
                {{ t_('status-placed') }}
              </MoleculeBadge>
              <MoleculeBadge v-else-if="isReactionStop(page.animalAvailability)" class="mb-2 lg:mb-3" :large="true" background="bg-app-gray-silverstone" color="text-white">
                {{ t_('status-reaction-stop') }}
              </MoleculeBadge>
              <MoleculeBadge v-else-if="page.animalMisfit" class="mb-2 lg:mb-3" :large="true" background="bg-app-lime" color="text-black">
                {{ t_('status-misfit') }}
              </MoleculeBadge>
              <MoleculeBadge v-else-if="page.animalHighlighted" class="mb-2 lg:mb-3" :large="true" background="bg-app-green" color="text-white">
                {{ t_('status-highlighted') }}
              </MoleculeBadge>
              <MoleculeBadge v-else-if="isNewInShelter(page.postDate, page.animalAvailability)" class="mb-2 lg:mb-3" background="bg-app-green" :large="true" color="text-white">
                {{ t_('status-new') }}
              </MoleculeBadge>
              <h1 v-if="animalType !== 'found'" class="text-black items-center mb-3 px-3 text-center text-sm font-bold leading-none uppercase lg:px-4 lg:text-xl xl:text-2xl">
                {{ page.title }}
              </h1>
            </div>
            <!-- Highlighted animal attributes -->
            <ul class="flex flex-col shrink-0 items-stretch -mx-4 w-full w-full font-semibold leading-none md:mx-0 md:text-sm lg:text-base">
              <li v-for="config of enabledHighlightConfigs.filter((config) => config.hasValues(page))" :key="config.handle" class="c-header-important-info-item text-black">
                <AtomIcon :icon="config.getIcon(page[config.handle])" class="c-header-important-info-icon" />
                <template v-if="config.showLabel">{{ config.label }}:</template>
                {{ config.getValues(page).join(', ') }}
              </li>
              <!-- animalCouple is not managed by the enabledHighlightConfigs because the links in the makes it special -->
              <li v-if="page.animalCouple.length" class="c-header-important-info-item">
                <AtomIcon icon="chain" class="c-header-important-info-icon" />
                <span>
                  {{ t_('couple') }}
                  <span v-for="(item, index) in page.animalCouple" :key="index">
                    <AtomLink :url="item.url" class="text-app-green hover:underline">{{ item.title }}</AtomLink>
                    <span v-if="index < page.animalCouple.length - 1" class="text-app-green">, </span>
                  </span>
                </span>
              </li>
            </ul>
          </div>

          <MoleculeCardText
            v-if="shelter"
            :show-border="true"
            class="relative z-0 hidden flex-col mt-4 pb-3 pt-4 px-3 w-full text-sm leading-tight rounded-xl md:flex md:col-span-6 md:col-start-7 md:row-start-1 lg:mt-8 lg:pb-4 lg:pt-6 lg:px-4 lg:text-base"
          >
            <h3 class="text-black mb-1 break-words font-semibold leading-none lg:mb-3 lg:font-bold lg:uppercase">{{ get(shelter, 'title') }}</h3>
            <address v-if="get(shelter, 'department.address')" class="text-black mb-3 w-full not-italic lg:mb-4">
              {{ shelter.department.address.streetName }}
              {{ shelter.department.address.streetNumber }}
              {{ shelter.department.address.streetNumberAddition }}<br />
              {{ shelter.department.address.postalCode }}
              {{ shelter.department.address.city }}
            </address>
            <div class="flex flex-col items-start w-full">
              <AtomButton
                v-if="departmentUrl"
                :url="departmentUrl"
                button-type="primary"
                :center-content="true"
                class="flex shrink-0 2xl:w-auto w-full text-xs lg:text-sm xl:text-base"
              >
                {{ t_('contact-website') }}
              </AtomButton>
            </div>
          </MoleculeCardText>

          <MoleculeShare
            class="relative z-0 hidden flex-col mt-4 mb-4 pb-3 pt-4 px-3 w-full text-sm leading-tight rounded-xl md:flex md:col-span-6 md:col-start-7 md:row-start-1 lg:mt-8 lg:pb-4 lg:pt-6 lg:px-4 lg:text-base"
            :title="t_('share-title', { name: page.title })"
            :text="t_('share-text', { name: page.title })"
            :url="page.url"
            :mobile-bg-white="true"
            :show-border="true"
          />
        </header>

        <div
          class="2xl:col-span-6 mb-8 px-0 max-w-full md:col-span-9 md:row-end-3 md:row-start-1 md:mt-8 md:px-8 md:bg-white md:rounded-xl"
          :class="{ 'md:pt-8': media.length || page.introduction }"
        >
          <div class="mb-4 px-4 max-w-full md:mb-8 md:px-0">
            <MoleculeImageCarousel :media="media" />
          </div>

          <!-- introduction text -->
          <div v-if="page.introduction">
            <div
              class="prose prose--app p-4 border-b-2 border-solid border-app-green-100 md:-mx-8 md:px-8"
              :class="{ 'md:border-t-0': !media.length, 'border-t-2': media.length }"
              v-html="page.introduction"
            />
          </div>

          <!-- youtube video -->
          <div v-if="get(page, 'youtube.code')" class="py-5 lg:pt-5 lg:pb-0 mb-4 px-4 max-w-full md:mb-8 md:px-0">
            <div class="aspect-video youtube rounded-xl overflow-hidden">
              <div class="w-full h-full" v-html="page.youtube.code" />
            </div>
          </div>

          <div class="px-6 sm:px-8 md:-mx-8 md:px-0 lg:px-6">
            <MoleculeAnimalDetailBlocks :show-as-steps="showBlocksAsSteps" :items="blocks">
              <template #attributes>
                <div v-if="page.description" class="prose prose--app mb-6 max-w-none font-bold md:mb-8 md:font-normal xl:mb-10" v-html="page.description" />
                <MoleculeAnimalMeetYourMatch
                  v-if="page.animalMeetYourMatch"
                  class="mb-10 mb-8 mt-3 w-full sm:w-auto md:mt-2 md:w-full lg:w-auto xl:mb-10 xl:mt-0"
                  v-bind="page.animalMeetYourMatch"
                />
                <h4 class="text-black mb-3 text-base font-bold leading-snug uppercase lg:text-lg">{{ t_('attributes-title') }}</h4>
                <div class="prose prose--app max-w-none mb-6">
                  <table>
                    <tbody>
                      <tr v-for="config of enabledDetailConfigs.filter((config) => config.hasValues(page))" :key="config.handle">
                        <th>{{ config.label }}</th>
                        <td>
                          {{ config.getValues(page).join(', ') }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="get(page, 'animalDogCategory.title') && get(page, 'animalDogCategory.infoUrl')" class="mb-6 max-w-none md:mb-8 xl:mb-10">
                  <AtomLink
                    :url="get(page, 'animalDogCategory.infoUrl')"
                    target="_blank"
                    class="hover:text-app-green font-bold text-black underline transform transition duration-200"
                  >
                    {{ t_('read-more-dog-category', { dogCategory: get(page, 'animalDogCategory.title') }) }}
                  </AtomLink>
                </div>
              </template>

              <template #prices>
                <div class="prose prose--app" v-html="get(shelter, 'pricesDetailDescription')" />
              </template>

              <template #contact="{ item }">
                <div v-if="contactDetailDescription" class="prose prose--app" v-html="contactDetailDescription" />
                <div class="my-4 text-black">{{ item.mailTo }}</div>
                <AtomButton
                  button-type="primary"
                  class="inline-block mb-2"
                  :url="`mailto:${encodeURI(item.mailTo)}?subject=${encodeURIComponent(item.mailSubject)}&body=${encodeURIComponent(item.mailBody)}`"
                >
                  <AtomIconMail class="shrink-0 mr-2 w-6 h-6 text-black" />
                  {{ t_('contact-mail-label') }}
                </AtomButton>
              </template>
            </MoleculeAnimalDetailBlocks>
          </div>
        </div>

        <!-- shelter info + share options -->
        <footer
          v-if="shelter"
          class="2xl:col-span-6 2xl:col-start-5 grid-cols-12 mb-4 px-4 md:grid md:gap-x-8 md:col-span-8 md:col-start-5 md:row-end-5 md:row-start-4 md:px-0 xl:col-span-8 xl:col-start-5"
        >
          <MoleculeCardText
            :title="shelter.title"
            :mobile-bg-white="false"
            :show-border="false"
            class="relative z-0 flex flex-col mb-12 mt-12 w-full bg-white rounded-xl md:hidden md:col-span-6 md:col-start-7 md:row-start-1 md:mx-0 md:pb-3 lg:pb-5"
          >
            <address v-if="get(shelter, 'department.address')" class="mb-3 w-full not-italic">
              {{ shelter.department.address.streetName }}
              {{ shelter.department.address.streetNumber }}
              {{ shelter.department.address.streetNumberAddition }}<br />
              {{ shelter.department.address.postalCode }}
              {{ shelter.department.address.city }}
            </address>
            <div class="flex flex-col items-start w-full">
              <AtomButton v-if="departmentUrl" :url="departmentUrl" button-type="primary" class="flex shrink-0 place-items-center">
                {{ t_('contact-website') }}
              </AtomButton>
            </div>
          </MoleculeCardText>
          <MoleculeShare
            class="relative z-0 flex flex-col mb-4 pt-4 pb-3 px-3 w-full text-sm leading-tight rounded-xl md:hidden"
            :title="t_('share-title', { name: page.title })"
            :text="t_('share-text', { name: page.title })"
            :url="page.url"
            :mobile-bg-white="true"
            :show-border="false"
          />
        </footer>
      </section>
      <OrganismCallToActionList v-if="ctas" :ctas="ctas" class="px-2 py-4 bg-app-green-100" />
    </main>
  </TemplateBase>
</template>

<script setup lang="ts">
import { computed, unref } from 'vue'
import get from 'lodash/get'
import { setupTranslationPrefixer } from '~/composables/util'
import { isAvailableSoon, isNewInShelter, isPlaced, isReactionStop, isReserved } from '@/assets/animal'
import { setupAnimalDetailConfigs, setupAnimalHighlightConfigs } from '@/assets/animal-attributes'
import { setupDepartment } from '~/assets/department'
import { useShelterStore } from '~/store/shelter'
import { useCtaStore } from '~/store/call-to-action'

interface Props {
  breadcrumbs?: Array<any>
  page?: any
}

const props = withDefaults(defineProps<Props>(), {
  breadcrumbs: () => [],
  page: {},
})

const shelterStore = useShelterStore()
const route = useRoute()
const shelter = computed(() => shelterStore.shelter)
const { t_ } = setupTranslationPrefixer('AnimalDetail', props.page.animalType)
const { getUrl } = setupDepartment()
const media = props.page.images.map((image: any) => ({ ...image, type: 'image' }))
const animalCategory = props.page.animalCategory.slug
const animalType = computed(() => {
  switch (unref(route).meta.site) {
    case 'mijnDierIsZoek':
      return unref(route).params.mdizCategory === 'vermiste-dieren' ? 'missing' : 'found'
    case 'ikZoekBaas':
    default:
      return 'available'
  }
})
const contactDetailDescription = computed(() => {
  for (const { domain, selectedCategories, description } of shelter.value?.contactDetailDescriptions || []) {
    if (domain === props.page.animalCategory.site.handle && selectedCategories.includes(props.page.animalCategory.slug)) {
      return description
    }
  }
  return undefined
})
const showBlocksAsSteps = props.page.animalType === 'available'
const blocks = computed(() =>
  [
    {
      enabled: true,
      isInitiallyOpen: true,
      slot: 'attributes',
      icon: props.page.animalType === 'available' ? 'heart' : 'check',
      label: t_('attributes-label'),
    },
    {
      enabled: props.page.animalType === 'available' && shelter.value?.pricesDetailDescription,
      isInitiallyOpen: false,
      slot: 'prices',
      icon: 'piggy-bank',
      label: t_('prices-label'),
    },
    {
      enabled: true,
      isInitiallyOpen: true,
      slot: 'contact',
      icon: 'phone',
      label: t_('contact-label'),
      mailTo: props.page.animalType === 'missing' ? 'websupport@dierenbescherming.nl' : shelter.value?.department?.email?.replace(/^mailto:/, ''),
      mailSubject: t_('contact-mail-subject', {
        category: props.page.animalCategory?.title,
        filenumber: props.page.filenumber,
        name: props.page.title,
        url: props.page.url,
      }),
      mailBody: t_('contact-mail-body', {
        category: props.page.animalCategory?.title,
        filenumber: props.page.filenumber,
        name: props.page.title,
        url: props.page.url,
      }),
    },
  ].filter(({ enabled }) => enabled),
)

const departmentUrl = computed(() => getUrl(shelter.value?.department))

const enabledDetailConfigs = setupAnimalDetailConfigs(animalCategory, animalType.value)
const enabledHighlightConfigs = setupAnimalHighlightConfigs(animalCategory, animalType.value)

const ctas = ref(null)

watchEffect(async () => {
  const site = unref(route).meta.site

  if (site === 'mijnDierIsZoek') {
    ctas.value = null
  } else {
    ctas.value = await useCtaStore().fetch(animalCategory)
  }
})
</script>

<style lang="scss" scoped>
.c-header-content-container {
  @apply -mx-4;
  width: calc(100% + theme('spacing.4') + theme('spacing.4'));

  @screen md {
    @apply w-full mx-auto pt-10;
    margin-top: -50px;
  }

  @screen lg {
    @apply pt-20;
    margin-top: -88px;
  }
}

.c-header-important-info-item {
  @apply flex items-center p-3 border-t-2 border-solid border-app-green-100;

  &:last-child {
    @apply border-b-2;
  }

  @screen md {
    &:last-child {
      @apply border-b-0;
    }
  }

  @screen lg {
    @apply p-4;
  }
}

:deep(.c-header-important-info-icon) {
  @apply shrink-0 w-5 h-5 text-app-green mr-3;

  @screen md {
    @apply w-4 h-4 mr-2;
  }

  @screen lg {
    @apply w-5 h-5 mr-3;
  }
}

.profile-picture {
  width: 100px;
  height: 100px;

  @screen lg {
    width: 152px;
    height: 152px;
  }

  @screen xl {
    width: 164px;
    height: 164px;
  }
}

.flipped-upsidedown {
  transform: rotate(180deg);
}

.favorite-link {
  text-underline-offset: theme('spacing.1');
  text-decoration-thickness: 2px;
  text-decoration-color: theme('colors.app.green');
}
</style>
